<script>
/**
 * 공지·FAQ 등록/수정 (meta.boardKind)
 */
import * as noticeApi from '@/api/notice.js'
import * as faqApi from '@/api/faq.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'SiteBoardFormPage',
  data() {
    return {
      isHq: false,
      loading: false,
      saving: false,
      form: {
        boardTitle: '',
        boardText: '',
        noticeYn: 'N',
        viewStartDate: '',
        viewEndDate: '',
      },
      /** 상세에서만 일시 표시 */
      metaInsertDt: null,
      metaUpdateDt: null,
    }
  },
  computed: {
    kind() {
      return this.$route.meta.boardKind || 'notice'
    },
    isNotice() {
      return this.kind === 'notice'
    },
    pageTitle() {
      if (this.isCreate) return this.isNotice ? '공지 등록' : 'FAQ 등록'
      return this.isNotice ? '공지 수정' : 'FAQ 수정'
    },
    isCreate() {
      return this.$route.path.endsWith('/new')
    },
    boardCode() {
      return this.$route.params.boardCode
    },
  },
  mounted() {
    this.isHq = getAdminInfo().adminType === 'HQ'
    if (!this.isHq) {
      this.$router.replace('/dashboard')
      return
    }
    if (!this.isCreate && this.boardCode) {
      this.load()
    }
  },
  async beforeRouteUpdate(to, from, next) {
    next()
    await this.$nextTick()
    if (!this.isHq) return
    if (!to.path.endsWith('/new') && to.params.boardCode) {
      await this.load()
    }
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const fn = this.isNotice ? noticeApi.getNoticeDetail : faqApi.getFaqDetail
        const res = await fn(this.boardCode)
        if (res.data?.code === 0 && res.data.data) {
          const d = res.data.data
          this.form.boardTitle = d.boardTitle || ''
          this.form.boardText = d.boardText || ''
          this.form.noticeYn = d.noticeYn === 'Y' ? 'Y' : 'N'
          this.form.viewStartDate = d.viewStartDate || ''
          this.form.viewEndDate = d.viewEndDate || ''
          this.metaInsertDt = d.insertDt ?? null
          this.metaUpdateDt = d.updateDt ?? null
        }
      } catch (e) {
        console.error('[SiteBoardFormPage] load', e)
      } finally {
        this.loading = false
      }
    },
    payload() {
      return {
        boardTitle: this.form.boardTitle.trim(),
        boardText: this.form.boardText.trim(),
        noticeYn: this.isNotice ? this.form.noticeYn : 'N',
        viewStartDate: this.form.viewStartDate || null,
        viewEndDate: this.form.viewEndDate || null,
      }
    },
    async save() {
      if (!this.form.boardTitle.trim() || !this.form.boardText.trim()) {
        return
      }
      this.saving = true
      try {
        if (this.isCreate) {
          const fn = this.isNotice ? noticeApi.createNotice : faqApi.createFaq
          const res = await fn(this.payload())
          if (res.data?.code === 0 && res.data.data?.boardCode) {
            const code = res.data.data.boardCode
            const path = this.isNotice ? `/notices/${code}` : `/faqs/${code}`
            await this.$router.replace(path)
            await this.load()
          }
        } else {
          const fn = this.isNotice ? noticeApi.updateNotice : faqApi.updateFaq
          const res = await fn(this.boardCode, this.payload())
          if (res.data?.code === 0) await this.load()
        }
      } catch (e) {
        console.error('[SiteBoardFormPage] save', e)
      } finally {
        this.saving = false
      }
    },
    async remove() {
      if (!this.boardCode || this.isCreate) return
      if (!window.confirm('삭제하시겠습니까?')) return
      this.saving = true
      try {
        const fn = this.isNotice ? noticeApi.deleteNotice : faqApi.deleteFaq
        const res = await fn(this.boardCode)
        if (res.data?.code === 0) {
          this.$router.push(this.isNotice ? '/notices' : '/faqs')
        }
      } catch (e) {
        console.error('[SiteBoardFormPage] remove', e)
      } finally {
        this.saving = false
      }
    },
    goList() {
      this.$router.push(this.isNotice ? '/notices' : '/faqs')
    },
    /** 상세: 날짜+시각 */
    formatDateTime(val) {
      if (!val) return '-'
      const s = String(val).replace('T', ' ')
      return s.length > 19 ? s.substring(0, 19) : s
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-2 mb-4">
      <div class="text-h5 font-weight-bold">{{ pageTitle }}</div>
      <div class="d-flex gap-2">
        <v-btn variant="text" @click="goList">목록</v-btn>
        <v-btn v-if="!isCreate" color="error" variant="tonal" :loading="saving" @click="remove">삭제</v-btn>
        <v-btn color="primary" variant="flat" :loading="saving" :disabled="loading" @click="save">저장</v-btn>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <v-card v-if="!isCreate && (metaInsertDt || metaUpdateDt)" rounded="lg" elevation="1" class="pa-4 mb-4">
      <div class="text-caption text-grey mb-1">등록·수정 일시 (상세 전용)</div>
      <div class="text-body-2">등록: {{ formatDateTime(metaInsertDt) }}</div>
      <div class="text-body-2">수정: {{ formatDateTime(metaUpdateDt) }}</div>
    </v-card>

    <v-card rounded="lg" elevation="1" class="pa-4">
      <v-switch
        v-if="isNotice"
        v-model="form.noticeYn"
        true-value="Y"
        false-value="N"
        color="primary"
        label="상단 고정 공지"
        hide-details
        class="mb-4"
      />
      <v-text-field
        v-model="form.boardTitle"
        label="제목"
        variant="outlined"
        density="comfortable"
        class="mb-4"
      />
      <v-textarea
        v-model="form.boardText"
        label="내용"
        variant="outlined"
        rows="12"
        class="mb-4"
      />
      <div class="d-flex flex-wrap gap-4">
        <v-text-field
          v-model="form.viewStartDate"
          label="게시 시작일 (선택)"
          type="date"
          variant="outlined"
          density="comfortable"
          clearable
          style="max-width: 200px"
        />
        <v-text-field
          v-model="form.viewEndDate"
          label="게시 종료일 (선택)"
          type="date"
          variant="outlined"
          density="comfortable"
          clearable
          style="max-width: 200px"
        />
      </div>
    </v-card>
  </div>
</template>
